import triggerBackup from "features/panelManagement/backups/actions/backup.triggerBackup";
import { useDispatch, useSelector } from "services/redux/rootReducer";
import {
    convertExceltoJSON,
    downloadJSONasExcel,
} from "../functions/excelOperations";
import { downloadJSONAsJSON } from "../functions/jsonOperations";
import { ImportExportType } from "../types/importExport";

interface Props<T> {
    data: T[];
    collectionName: string;
    exportFileName: string;
    unflattenMapperFunction: (flattenedElement: Record<string, any>) => T;
    flattenMapperFunction: (element: T) => Record<string, any>;
    verificationFunction: (element: T) => Promise<string>;
    onImportSubmitCallback: (data: T[]) => Promise<void>;
}

const useImportExport = <T extends Record<string, any>>({
    data,
    collectionName,
    exportFileName,
    unflattenMapperFunction,
    flattenMapperFunction,
    verificationFunction,
    onImportSubmitCallback,
}: Props<T>) => {
    const dispatch = useDispatch();
    const { importExportConfig, indexer } = useSelector(
        (state) => state.editor
    );

    const handleImportData = async (rawData: string | File) => {
        try {
            await dispatch(triggerBackup([collectionName]));
            let parsedJSONArray: T[];
            switch (importExportConfig.importType) {
                case ImportExportType.EXCEL:
                    parsedJSONArray = (
                        (await convertExceltoJSON(rawData as File)) as Record<
                            string,
                            any
                        >[]
                    ).map(unflattenMapperFunction);
                    break;
                case ImportExportType.JSON:
                default:
                    parsedJSONArray = JSON.parse(rawData as string);
                    break;
            }

            await Promise.all(
                parsedJSONArray.map(async (element: T, idx) => {
                    try {
                        await verificationFunction(element);
                    } catch (e) {
                        throw new Error(
                            `Found error ${e} at element number ${idx + 2}` // header = 1, 1-based indexing = 1
                        );
                    }
                })
            );

            await onImportSubmitCallback(parsedJSONArray);

            alert("Success");
        } catch (err) {
            alert(err);
        }
    };

    const handleExportData = (indexers: string[]) => {
        const selectedData = data.filter((element) =>
            indexers.includes(element[indexer])
        );
        switch (importExportConfig.exportType) {
            case ImportExportType.EXCEL:
                downloadJSONasExcel(
                    selectedData.map(flattenMapperFunction),
                    exportFileName
                );
                return;
            case ImportExportType.JSON:
                downloadJSONAsJSON(selectedData, exportFileName);
                return;
        }
    };

    return [handleImportData, handleExportData] as const;
};

export default useImportExport;
